import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { marked } from "marked";
import { toast } from "sonner";
import { ArrowLeft, Bold, Italic, Quote, Heading1, Heading2, Heading3, List, ListOrdered, Link2, Code, Minus, X, Save, Eye, Send } from "lucide-react";
import { isAdminAuthed, adminFetchPosts, adminCreatePost, adminUpdatePost, CATEGORIES, CATEGORY_COLOR } from "@/lib/blogApi";

const AUTHORS = [
  { name: "Madhu Babu Alegula", title: "Co-Founder & CEO, Spiruva", initials: "MA" },
  { name: "Sripal Reddy Molugu", title: "Co-Founder & CTO, Spiruva", initials: "SR" },
  { name: "Ganga Reddy Kallem", title: "Co-Founder & COO, Spiruva", initials: "GR" },
  { name: "Spiruva Research Team", title: "Industry Intelligence Desk", initials: "SR" },
];

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

const AdminEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const authed = isAdminAuthed();
  const textareaRef = useRef(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [tagInput, setTagInput] = useState("");

  const [form, setForm] = useState({
    title: "",
    slug: "",
    metaTitle: "",
    metaDescription: "",
    excerpt: "",
    content: "",
    category: CATEGORIES[0],
    tags: [],
    author: AUTHORS[3],
    isFeatured: false,
    isPublished: false,
  });

  // Load existing
  useEffect(() => {
    if (!authed) return;
    if (!isEdit) {
      const saved = localStorage.getItem("spiruva_draft_new");
      if (saved) { try { setForm(JSON.parse(saved)); } catch {} }
      return;
    }
    let alive = true;
    adminFetchPosts()
      .then((all) => {
        const p = all.find((x) => x.id === id);
        if (alive && p) {
          setForm({
            ...p,
            author: p.author || AUTHORS[3],
            tags: p.tags || [],
          });
        }
      })
      .catch(() => toast.error("Failed to load post"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, isEdit, authed]);

  // Auto-slug
  useEffect(() => {
    if (!isEdit && !form.slug && form.title) {
      setForm((f) => ({ ...f, slug: slugify(form.title) }));
    }
  }, [form.title, isEdit, form.slug]);

  // Autosave drafts every 30s (only when creating)
  useEffect(() => {
    if (isEdit) return;
    const t = setInterval(() => {
      localStorage.setItem("spiruva_draft_new", JSON.stringify(form));
      setLastSaved(new Date());
    }, 30000);
    return () => clearInterval(t);
  }, [form, isEdit]);

  const wordCount = useMemo(() => (form.content.match(/\w+/g) || []).length, [form.content]);
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const insertAtCursor = (before, after = "") => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const selected = form.content.slice(start, end);
    const next = form.content.slice(0, start) + before + selected + after + form.content.slice(end);
    setField("content", next);
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = start + before.length + selected.length + after.length;
      ta.setSelectionRange(cursor, cursor);
    });
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) setField("tags", [...form.tags, t]);
    setTagInput("");
  };

  const removeTag = (t) => setField("tags", form.tags.filter((x) => x !== t));

  const persist = async (publish) => {
    if (!form.title || !form.excerpt || !form.content) {
      toast.error("Title, excerpt, and content are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        isPublished: publish !== undefined ? publish : form.isPublished,
        slug: form.slug || slugify(form.title),
        metaTitle: form.metaTitle || form.title,
        metaDescription: form.metaDescription || form.excerpt,
        author: form.author,
      };
      if (isEdit) {
        await adminUpdatePost(id, payload);
        toast.success("Post updated");
      } else {
        const created = await adminCreatePost(payload);
        toast.success("Post created");
        localStorage.removeItem("spiruva_draft_new");
        navigate(`/admin/posts/edit/${created.id}`);
        return;
      }
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const previewHtml = useMemo(() => marked.parse(form.content || ""), [form.content]);

  const TOOLBAR = [
    { icon: Bold, label: "Bold", action: () => insertAtCursor("**", "**") },
    { icon: Italic, label: "Italic", action: () => insertAtCursor("_", "_") },
    { icon: Heading1, label: "H1", action: () => insertAtCursor("\n# ") },
    { icon: Heading2, label: "H2", action: () => insertAtCursor("\n## ") },
    { icon: Heading3, label: "H3", action: () => insertAtCursor("\n### ") },
    { icon: Quote, label: "Quote", action: () => insertAtCursor("\n> ") },
    { icon: List, label: "Bullet List", action: () => insertAtCursor("\n- ") },
    { icon: ListOrdered, label: "Numbered List", action: () => insertAtCursor("\n1. ") },
    { icon: Link2, label: "Link", action: () => insertAtCursor("[", "](https://)") },
    { icon: Code, label: "Code", action: () => insertAtCursor("`", "`") },
    { icon: Minus, label: "Divider", action: () => insertAtCursor("\n\n---\n\n") },
  ];

  if (!authed) return <Navigate to="/admin" replace />;
  if (loading) return <div className="pt-[120px] container-wide pb-32 text-brand-black/60">Loading…</div>;

  return (
    <div className="pt-[72px] bg-brand-cream/40 min-h-screen" data-testid="page-admin-editor">
      <div className="container-wide py-10">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-brand-black/65 hover:text-brand-teal mb-6">
          <ArrowLeft size={14} /> Back to Dashboard
        </Link>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <p className="eyebrow mb-2">◦  {isEdit ? "Edit Post" : "New Post"}</p>
            <h1 className="font-display text-4xl text-brand-black">{form.title || "Untitled Post"}</h1>
          </div>
          <div className="flex items-center gap-4 text-xs text-brand-black/50 font-mono uppercase tracking-widest">
            <span>{wordCount} words</span>
            <span>·</span>
            <span>{readingTime} min read</span>
            {lastSaved && <><span>·</span><span>Auto-saved {lastSaved.toLocaleTimeString()}</span></>}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* EDITOR */}
          <div className="xl:col-span-7 space-y-6">
            <div className="bg-white border border-brand-green/15 p-6 space-y-4">
              <div>
                <Label>Title</Label>
                <input type="text" value={form.title} onChange={(e) => setField("title", e.target.value)} data-testid="editor-title" className="w-full mt-1 px-4 py-3 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-lg font-display" />
              </div>
              <div>
                <Label>Slug · <span className="text-brand-teal">/blog/{form.slug || "..."}</span></Label>
                <input type="text" value={form.slug} onChange={(e) => setField("slug", slugify(e.target.value))} data-testid="editor-slug" className="w-full mt-1 px-4 py-3 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm font-mono" />
              </div>
              <div>
                <Label>Excerpt (2–3 sentences shown on cards)</Label>
                <textarea rows={3} value={form.excerpt} onChange={(e) => setField("excerpt", e.target.value)} data-testid="editor-excerpt" className="w-full mt-1 px-4 py-3 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm resize-none" />
              </div>
            </div>

            {/* SEO */}
            <details open className="bg-white border border-brand-green/15">
              <summary className="cursor-pointer p-5 font-display text-xl text-brand-black">SEO</summary>
              <div className="px-6 pb-6 space-y-4">
                <div>
                  <Label>Meta Title <span className={`font-mono text-[10px] ${form.metaTitle.length > 60 ? "text-red-700" : "text-brand-black/50"}`}>({form.metaTitle.length}/60)</span></Label>
                  <input type="text" value={form.metaTitle} onChange={(e) => setField("metaTitle", e.target.value)} data-testid="editor-meta-title" className="w-full mt-1 px-4 py-2.5 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm" />
                </div>
                <div>
                  <Label>Meta Description <span className={`font-mono text-[10px] ${form.metaDescription.length > 155 ? "text-red-700" : "text-brand-black/50"}`}>({form.metaDescription.length}/155)</span></Label>
                  <textarea rows={3} value={form.metaDescription} onChange={(e) => setField("metaDescription", e.target.value)} data-testid="editor-meta-desc" className="w-full mt-1 px-4 py-2.5 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm resize-none" />
                </div>
                {/* Google preview */}
                <div className="border border-brand-green/15 p-4 bg-brand-cream/30">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/45 mb-2">Google Search Preview</p>
                  <p className="text-[12px] text-brand-black/55">www.spiruva.com › blog › {form.slug}</p>
                  <p className="text-[18px] text-[#1a0dab] mt-1 line-clamp-1">{form.metaTitle || form.title || "Untitled"}</p>
                  <p className="text-[13px] text-brand-black/70 mt-1 line-clamp-2">{form.metaDescription || form.excerpt}</p>
                </div>
              </div>
            </details>

            {/* META */}
            <div className="bg-white border border-brand-green/15 p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <select value={form.category} onChange={(e) => setField("category", e.target.value)} data-testid="editor-category" className="w-full mt-1 px-4 py-2.5 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Author</Label>
                  <select value={form.author.name} onChange={(e) => setField("author", AUTHORS.find((a) => a.name === e.target.value))} data-testid="editor-author" className="w-full mt-1 px-4 py-2.5 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm">
                    {AUTHORS.map((a) => <option key={a.name}>{a.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Tags (type + Enter)</Label>
                <div className="mt-1 flex flex-wrap gap-1.5 px-3 py-2 bg-brand-cream/40 border border-brand-green/20">
                  {form.tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 bg-brand-teal/10 text-brand-teal">
                      {t} <button onClick={() => removeTag(t)} aria-label={`Remove tag ${t}`}><X size={11} /></button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                    data-testid="editor-tags"
                    placeholder="Add tag..."
                    className="flex-1 min-w-[80px] bg-transparent text-sm focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Toggle label="Featured" value={form.isFeatured} onChange={(v) => setField("isFeatured", v)} testid="editor-featured" />
                <Toggle label="Published" value={form.isPublished} onChange={(v) => setField("isPublished", v)} testid="editor-published" />
              </div>
            </div>

            {/* CONTENT */}
            <div className="bg-white border border-brand-green/15">
              <div className="flex flex-wrap items-center gap-1 p-2 border-b border-brand-green/15">
                {TOOLBAR.map(({ icon: Icon, label, action }) => (
                  <button key={label} title={label} aria-label={label} onClick={action} className="p-2 hover:bg-brand-cream text-brand-black/70 hover:text-brand-teal">
                    <Icon size={14} />
                  </button>
                ))}
              </div>
              <textarea
                ref={textareaRef}
                rows={20}
                value={form.content}
                onChange={(e) => setField("content", e.target.value)}
                data-testid="editor-content"
                placeholder="Write your post in Markdown. Use ## for section headers, **bold**, _italic_, > for callouts."
                className="w-full px-6 py-5 bg-white font-mono text-sm focus:outline-none resize-none min-h-[500px]"
              />
            </div>
          </div>

          {/* LIVE PREVIEW */}
          <div className="xl:col-span-5">
            <div className="xl:sticky xl:top-[100px]">
              <p className="eyebrow mb-3">◦  Live Preview</p>
              <div className="bg-white border border-brand-green/15 p-6 max-h-[80vh] overflow-y-auto">
                <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: CATEGORY_COLOR[form.category]?.hex }}>{form.category}</span>
                <h2 className="font-display text-3xl text-brand-black mt-2 leading-tight">{form.title || "Untitled Post"}</h2>
                <p className="text-sm text-brand-black/60 mt-2">{form.excerpt}</p>
                <div className="prose-spiruva mt-6 text-sm" dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
            </div>
          </div>
        </div>

        {/* Sticky action bar */}
        <div className="sticky bottom-0 mt-8 -mx-6 md:-mx-10 lg:-mx-16 px-6 md:px-10 lg:px-16 py-4 bg-white border-t border-brand-green/15 flex items-center justify-between z-10">
          <p className="text-xs text-brand-black/55">{wordCount} words · {readingTime} min read</p>
          <div className="flex gap-3">
            <button onClick={() => persist(false)} disabled={saving} data-testid="editor-save-draft" className="btn-secondary"><Save size={14} /> Save Draft</button>
            {form.slug && isEdit && (
              <a href={`/blog/${form.slug}`} target="_blank" rel="noopener noreferrer" className="btn-secondary"><Eye size={14} /> Preview</a>
            )}
            <button onClick={() => persist(true)} disabled={saving} data-testid="editor-publish" className="btn-primary"><Send size={14} /> {form.isPublished ? "Update" : "Publish"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Label = ({ children }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55">{children}</span>
);

const Toggle = ({ label, value, onChange, testid }) => (
  <div className="flex items-center justify-between p-3 bg-brand-cream/40 border border-brand-green/20">
    <Label>{label}</Label>
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      data-testid={testid}
      className={`w-10 h-5 relative transition ${value ? "bg-brand-green" : "bg-brand-black/15"}`}
    >
      <span className={`absolute top-0.5 w-4 h-4 bg-white transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
    </button>
  </div>
);

export default AdminEditor;
